import { useEffect, useRef } from "react";
import { SoftBox } from "../game/engine/roundedBoxes";
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import type { AvatarConfig, ThemeId, TimeOfDay } from "@shared/types";
import { themeFor } from "../game/engine/Scene";
import { SEED_LESSONS } from "../data/seed-lessons";
import { buildRunnerCharacter } from "../game/engine/runnerCharacter";
import { isLowQuality } from "../game/engine/quality";
import { AvatarPreview } from "./AvatarEditor";

interface Props {
  avatar: AvatarConfig;
  theme: ThemeId;
  timeOfDay: TimeOfDay;
}

const LOW = isLowQuality();

export function MainMenuHero({ avatar, theme, timeOfDay }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (LOW) return; // low tier: no WebGL hero, cheap CSS avatar instead
    const canvas = ref.current;
    if (!canvas) return;

    const colors = themeFor(theme, timeOfDay);
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    const scene = new THREE.Scene();
    scene.background = makeLobbyBackdropTexture();
    scene.fog = null;

    // PMREM studio environment → glossy plastic reflections on the hero.
    let envRT: THREE.WebGLRenderTarget | null = null;
    try {
      const pmrem = new THREE.PMREMGenerator(renderer);
      envRT = pmrem.fromScene(new RoomEnvironment(), 0.04);
      scene.environment = envRT.texture;
      scene.environmentIntensity = 0.65;
      pmrem.dispose();
    } catch { /* reflections are a nice-to-have */ }

    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 80);
    camera.position.set(0, 3.7, 9.2);
    camera.lookAt(0, 1.65, 0);

    scene.add(new THREE.HemisphereLight(colors.sky, colors.hemiGround, 1.0));
    const sun = new THREE.DirectionalLight(0xfff1c8, 1.7);
    sun.position.set(-8, 18, 10);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.bias = -0.0006;
    sun.shadow.radius = 5;
    scene.add(sun);
    // Pink rim from behind for the toy-studio pop the mockup has.
    const rim = new THREE.PointLight(0xff5fc8, 3.6, 26, 1.6);
    rim.position.set(5, 5, -5);
    scene.add(rim);

    const rings = buildLobbyRings();
    scene.add(rings);
    const platform = buildLobbyPlatform();
    scene.add(platform);
    const runner = buildMenuRunner(avatar);
    const runnerBaseY = 0.92;
    runner.position.set(0, runnerBaseY, 0.25);
    runner.rotation.y = 0.12;
    runner.scale.setScalar(0.82);
    scene.add(runner);

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / Math.max(1, h);
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);

    let raf = 0;
    let spinVelocity = 0;
    let leftHeld = false;
    let rightHeld = false;
    let jumpStartedAt = -100;
    const isTypingTarget = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
    };
    const triggerJump = () => {
      jumpStartedAt = (performance.now() - start) * 0.001;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      if (event.key === "ArrowLeft" || event.key.toLowerCase() === "a") {
        leftHeld = true;
        event.preventDefault();
      } else if (event.key === "ArrowRight" || event.key.toLowerCase() === "d") {
        rightHeld = true;
        event.preventDefault();
      } else if (event.code === "Space") {
        triggerJump();
        event.preventDefault();
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" || event.key.toLowerCase() === "a") leftHeld = false;
      if (event.key === "ArrowRight" || event.key.toLowerCase() === "d") rightHeld = false;
    };
    let dragActive = false;
    let dragMoved = false;
    let lastX = 0;
    let lastT = 0;
    const DRAG_THRESHOLD = 5;
    const onPointerDown = (event: PointerEvent) => {
      dragActive = true;
      dragMoved = false;
      lastX = event.clientX;
      lastT = performance.now();
      canvas.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    };
    const onPointerMove = (event: PointerEvent) => {
      if (!dragActive) return;
      const dx = event.clientX - lastX;
      const dt = Math.max(8, performance.now() - lastT);
      if (!dragMoved && Math.abs(dx) > DRAG_THRESHOLD) dragMoved = true;
      if (dragMoved) {
        // Convert px/ms drag into rotation velocity. Negative because dragging
        // right should rotate the character to face right (clockwise from above).
        const radiansPerPx = 0.012;
        spinVelocity = -dx * radiansPerPx * (16 / dt);
        runner.rotation.y -= dx * radiansPerPx;
      }
      lastX = event.clientX;
      lastT = performance.now();
    };
    const onPointerUp = (event: PointerEvent) => {
      if (!dragActive) return;
      dragActive = false;
      canvas.releasePointerCapture?.(event.pointerId);
      if (!dragMoved) triggerJump();
      // If they dragged, let spinVelocity decay naturally for ease-out.
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerUp);

    const start = performance.now();
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      const t = (now - start) * 0.001;
      const spinInput = (rightHeld ? 1 : 0) - (leftHeld ? 1 : 0);
      spinVelocity += spinInput * 0.045;
      // While the user is actively dragging, do NOT decay (the move handler
      // owns rotation). Otherwise apply ease-out damping so the spin slowly
      // coasts to a stop.
      if (!dragActive) {
        spinVelocity *= spinInput ? 0.94 : 0.965;
        runner.rotation.y += spinVelocity;
      }
      spinVelocity = THREE.MathUtils.clamp(spinVelocity, -0.6, 0.6);
      if (Math.abs(spinVelocity) < 0.0005) spinVelocity = 0;
      const jumpAge = t - jumpStartedAt;
      const jumpT = jumpAge >= 0 && jumpAge < 0.72 ? jumpAge / 0.72 : 1;
      const jumpLift = jumpT < 1 ? Math.sin(jumpT * Math.PI) * 0.78 : 0;
      runner.position.y = runnerBaseY + jumpLift;
      animateRunnerLegacy(runner, t, jumpT < 1, jumpT);
      rings.rotation.z = Math.sin(t * 0.18) * 0.025;
      platform.rotation.y = Math.sin(t * 0.7) * 0.05;
      scene.traverse((o) => {
        if (o.userData.spin) o.rotation.y += o.userData.spin;
        if (o.userData.floatBase != null) {
          o.position.y = o.userData.floatBase + Math.sin(t * 2.2 + o.userData.phase) * 0.18;
        }
      });
      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerUp);
      envRT?.dispose();
      renderer.dispose();
      scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          const mat = obj.material;
          if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
          else mat.dispose();
        }
      });
    };
  }, [avatar, theme, timeOfDay]);

  if (LOW) {
    return (
      <div className="home-hero-canvas home-hero-lite" aria-hidden="true">
        <AvatarPreview cfg={avatar} large />
      </div>
    );
  }
  return <canvas ref={ref} className="home-hero-canvas" aria-hidden="true" />;
}

function buildLevelScene(scene: THREE.Scene, colors: ReturnType<typeof themeFor>) {
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(95, 80),
    new THREE.MeshStandardMaterial({ color: colors.ground, roughness: 0.85 }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(0, -0.04, -18);
  ground.receiveShadow = true;
  scene.add(ground);

  const water = new THREE.Mesh(
    new THREE.PlaneGeometry(70, 42),
    new THREE.MeshStandardMaterial({
      color: colors.id === "space" ? 0x161b4a : 0x22b8e8,
      roughness: 0.38,
      transparent: true,
      opacity: colors.id === "space" ? 0.35 : 0.78,
    }),
  );
  water.rotation.x = -Math.PI / 2;
  water.position.set(0, -0.02, -18);
  scene.add(water);

  const pathMat = new THREE.MeshStandardMaterial({ color: colors.pathLight, roughness: 0.78 });
  const pathDark = new THREE.MeshStandardMaterial({ color: colors.pathDark, roughness: 0.78 });
  for (let i = 0; i < 18; i++) {
    const tile = new THREE.Mesh(new SoftBox(4.3 + i * 0.18, 0.18, 1.55), i % 2 ? pathMat : pathDark);
    tile.position.set(0, 0.08, 2.5 - i * 1.55);
    tile.receiveShadow = true;
    scene.add(tile);
  }

  const railMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.5 });
  for (const x of [-2.45, 2.45]) {
    const rail = new THREE.Mesh(new SoftBox(0.18, 0.55, 28), railMat);
    rail.position.set(x, 0.45, -10.2);
    rail.castShadow = true;
    scene.add(rail);
  }

  const gate = buildWordGate();
  gate.position.set(0, 0, -9.8);
  gate.scale.setScalar(0.9);
  scene.add(gate);

  for (let i = 0; i < 9; i++) {
    const coin = new THREE.Mesh(
      new THREE.CylinderGeometry(0.18, 0.18, 0.05, 18),
      new THREE.MeshStandardMaterial({ color: 0xffd93d, metalness: 0.45, roughness: 0.28 }),
    );
    coin.position.set((i % 3 - 1) * 0.7, 0.58, -2.8 - i * 0.9);
    coin.rotation.z = Math.PI / 2;
    coin.castShadow = true;
    coin.userData.spin = 0.035;
    scene.add(coin);
  }

  const star = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.52, 0),
    new THREE.MeshStandardMaterial({
      color: 0xffd93d,
      emissive: 0xffb020,
      emissiveIntensity: 0.55,
      flatShading: true,
    }),
  );
  star.position.set(0.8, 1.1, -1.6);
  star.userData.floatBase = star.position.y;
  star.userData.phase = 1.3;
  star.userData.spin = 0.018;
  scene.add(star);

  for (let i = 0; i < 18; i++) {
    const side = i % 2 === 0 ? -1 : 1;
    const tree = colors.id === "space" ? buildCrystalTree(i) : buildMenuTree(colors.id);
    tree.position.set(side * (4.3 + (i % 4) * 1.15), 0, 1.6 - i * 1.4);
    tree.scale.setScalar(0.75 + (i % 3) * 0.12);
    scene.add(tree);
  }

  for (let i = 0; i < 7; i++) {
    const mountain = new THREE.Mesh(
      new THREE.ConeGeometry(3.6 + i * 0.25, 7 + i * 0.8, 4),
      new THREE.MeshStandardMaterial({
        color: colors.id === "snow" ? 0xe8f2ff : colors.ground.clone().lerp(new THREE.Color(0xffffff), 0.32),
        flatShading: true,
      }),
    );
    mountain.position.set(-18 + i * 6.5, 3.0, -33 - (i % 2) * 5);
    mountain.rotation.y = Math.PI / 4;
    scene.add(mountain);
  }
}

function buildWordGate() {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({
    color: 0x4caf50,
    emissive: 0x37b73d,
    emissiveIntensity: 0.45,
  });
  for (const x of [-1.85, 1.85]) {
    const post = new THREE.Mesh(new SoftBox(0.28, 2.5, 0.28), mat);
    post.position.set(x, 1.25, 0);
    g.add(post);
  }
  const top = new THREE.Mesh(new SoftBox(4.4, 0.42, 0.32), mat);
  top.position.y = 2.55;
  g.add(top);

  const sign = new THREE.Mesh(
    new SoftBox(2.6, 0.48, 0.08),
    new THREE.MeshStandardMaterial({ color: 0x1f9d55, emissive: 0x1f9d55, emissiveIntensity: 0.4 }),
  );
  sign.position.set(0, 2.6, 0.22);
  g.add(sign);

  const label = new THREE.Mesh(
    new THREE.PlaneGeometry(2.25, 0.38),
    new THREE.MeshBasicMaterial({ map: makeTextTexture("WORD BOOST"), transparent: true }),
  );
  label.position.set(0, 2.62, 0.27);
  g.add(label);
  return g;
}

function buildMenuTree(theme: string) {
  const g = new THREE.Group();
  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(0.18, 0.28, 1.8, 7),
    new THREE.MeshStandardMaterial({ color: 0x7a4a28 }),
  );
  trunk.position.y = 0.9;
  trunk.castShadow = true;
  g.add(trunk);
  const leafColor =
    theme === "candy" ? 0xff8bc4 : theme === "snow" ? 0xeaf6ff : theme === "desert" ? 0x2f7d3a : 0x1f8a3b;
  const leaves = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.82, 0),
    new THREE.MeshStandardMaterial({ color: leafColor, flatShading: true }),
  );
  leaves.position.y = 2.1;
  leaves.castShadow = true;
  g.add(leaves);
  return g;
}

function buildCrystalTree(i: number) {
  const g = new THREE.Group();
  const crystal = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.75, 0),
    new THREE.MeshStandardMaterial({ color: i % 2 ? 0x6ec5ff : 0x9b5cf6, emissive: 0x172554, flatShading: true }),
  );
  crystal.position.y = 1.4;
  g.add(crystal);
  return g;
}

function buildLobbyRings() {
  const g = new THREE.Group();
  const ringGeo = new THREE.RingGeometry(1, 1.035, 96);
  for (let i = 0; i < 9; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color: i % 2 ? 0xffe35a : 0xfff58d,
      transparent: true,
      opacity: i % 2 ? 0.32 : 0.22,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(ringGeo, mat);
    ring.position.set(0, 2.0, -3.8 - i * 0.05);
    ring.scale.setScalar(1.35 + i * 0.72);
    g.add(ring);
  }
  return g;
}

function buildLobbyPlatform() {
  const g = new THREE.Group();

  // Wide purple skirt / base (bottom tier)
  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(2.55, 2.85, 0.45, 72),
    new THREE.MeshStandardMaterial({
      color: 0x7c3aed,
      emissive: 0x4c1d95,
      emissiveIntensity: 0.18,
      roughness: 0.42,
      metalness: 0.12,
    }),
  );
  base.position.y = 0.0;
  base.castShadow = true;
  base.receiveShadow = true;
  g.add(base);

  // Mid purple band, slightly inset
  const mid = new THREE.Mesh(
    new THREE.CylinderGeometry(2.32, 2.5, 0.22, 72),
    new THREE.MeshStandardMaterial({
      color: 0x9b5cf6,
      emissive: 0x6d28d9,
      emissiveIntensity: 0.16,
      roughness: 0.38,
      metalness: 0.14,
    }),
  );
  mid.position.y = 0.28;
  mid.castShadow = true;
  mid.receiveShadow = true;
  g.add(mid);

  // White rim spacer
  const rimDeck = new THREE.Mesh(
    new THREE.CylinderGeometry(2.18, 2.26, 0.12, 72),
    new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.35, metalness: 0.18 }),
  );
  rimDeck.position.y = 0.45;
  rimDeck.receiveShadow = true;
  g.add(rimDeck);

  // Pink top deck (the stage)
  const top = new THREE.Mesh(
    new THREE.CylinderGeometry(2.02, 2.12, 0.32, 72),
    new THREE.MeshStandardMaterial({
      color: 0xff5fd2,
      emissive: 0xff2fb9,
      emissiveIntensity: 0.22,
      roughness: 0.34,
      metalness: 0.14,
    }),
  );
  top.position.y = 0.66;
  top.castShadow = true;
  top.receiveShadow = true;
  g.add(top);

  // Outer torus rim around top edge (white highlight)
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(2.08, 0.07, 12, 96),
    new THREE.MeshStandardMaterial({
      color: 0xffffff,
      emissive: 0xffd9f3,
      emissiveIntensity: 0.18,
      roughness: 0.22,
      metalness: 0.3,
    }),
  );
  rim.position.y = 0.82;
  rim.rotation.x = Math.PI / 2;
  g.add(rim);

  // Subtle glow disc at the very top so the character pops
  const glow = new THREE.Mesh(
    new THREE.CircleGeometry(1.98, 64),
    new THREE.MeshBasicMaterial({
      color: 0xffc0ec,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
    }),
  );
  glow.rotation.x = -Math.PI / 2;
  glow.position.y = 0.83;
  g.add(glow);

  return g;
}

function buildWordIconPattern() {
  const g = new THREE.Group();
  const urls = SEED_LESSONS.flatMap((lesson) => lesson.words.map((w) => w.imageUrl)).filter(Boolean) as string[];
  const selected = urls.filter((_, i) => i % 3 === 0).slice(0, 18);
  selected.forEach((url, i) => {
    const tex = iconTexture(url);
    const mat = new THREE.SpriteMaterial({
      map: tex,
      transparent: true,
      opacity: 0.4,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(mat);
    const col = i % 6;
    const row = Math.floor(i / 6);
    sprite.position.set(-8 + col * 3.2 + (row % 2) * 1.2, 1.0 + row * 1.1, -4.8 - row * 0.15);
    sprite.scale.setScalar(0.68 + (i % 3) * 0.1);
    g.add(sprite);
  });
  return g;
}

function roundedCapsule(
  radius: number,
  length: number,
  color: THREE.ColorRepresentation,
  axis: "x" | "y" | "z" = "y",
) {
  const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.58, metalness: 0.04 });
  const mesh = new THREE.Mesh(new THREE.CapsuleGeometry(radius, length, 16, 24), mat);
  if (axis === "x") mesh.rotation.z = Math.PI / 2;
  if (axis === "z") mesh.rotation.x = Math.PI / 2;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function buildMenuRunner(avatar: AvatarConfig) {
  // Use the shared runner geometry so the menu hero matches the in-game
  // player + the podium screen exactly. No arms (in-game player has none),
  // so the menu's idle animation animates the body + legs only.
  const parts = buildRunnerCharacter(avatar);
  parts.group.userData.parts = { body: parts.body, ll: parts.leftLeg, rl: parts.rightLeg };
  return parts.group;
}

function buildMenuHat(id: string): THREE.Object3D {
  // Y positions match the in-game Player hats. The MainMenu runner has the
  // same face geometry (sphere(0.38) scaled (1.05, 1.18, 0.15) at y=2.05),
  // so head top is at ~2.5 and hats sit a touch above that.
  const g = new THREE.Group();
  switch (id) {
    case "cap-blue":
    case "cap-red": {
      const color = id === "cap-blue" ? 0x2563eb : 0xdc2626;
      const mat = new THREE.MeshStandardMaterial({ color });
      const crown = new THREE.Mesh(new THREE.BoxGeometry(0.78, 0.32, 0.78), mat);
      crown.position.set(0, 2.7, 0);
      g.add(crown);
      const brim = new THREE.Mesh(new THREE.BoxGeometry(0.78, 0.08, 0.45), mat);
      brim.position.set(0, 2.56, 0.5);
      g.add(brim);
      break;
    }
    case "beanie": {
      const beanie = new THREE.Mesh(
        new THREE.BoxGeometry(0.82, 0.4, 0.82),
        new THREE.MeshStandardMaterial({ color: 0xef476f }),
      );
      beanie.position.set(0, 2.74, 0);
      g.add(beanie);
      const pom = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 8, 8),
        new THREE.MeshStandardMaterial({ color: 0xffffff }),
      );
      pom.position.set(0, 3.0, 0);
      g.add(pom);
      break;
    }
    case "crown": {
      const goldMat = new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.6, roughness: 0.3 });
      const band = new THREE.Mesh(new THREE.BoxGeometry(0.78, 0.18, 0.78), goldMat);
      band.position.set(0, 2.63, 0);
      g.add(band);
      for (let i = -1; i <= 1; i++) {
        for (let j = -1; j <= 1; j++) {
          if (Math.abs(i) + Math.abs(j) !== 1) continue;
          const spike = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.3, 4), goldMat);
          spike.position.set(i * 0.3, 2.83, j * 0.3);
          g.add(spike);
        }
      }
      break;
    }
    case "party-hat": {
      const cone = new THREE.Mesh(
        new THREE.ConeGeometry(0.42, 0.95, 16),
        new THREE.MeshStandardMaterial({ color: 0xa855f7, roughness: 0.45 }),
      );
      cone.position.set(0, 2.96, 0);
      g.add(cone);
      const tip = new THREE.Mesh(
        new THREE.SphereGeometry(0.13, 10, 8),
        new THREE.MeshStandardMaterial({ color: 0xffd166 }),
      );
      tip.position.set(0, 3.48, 0);
      g.add(tip);
      break;
    }
    case "halo": {
      const halo = new THREE.Mesh(
        new THREE.TorusGeometry(0.42, 0.08, 12, 28),
        new THREE.MeshStandardMaterial({
          color: 0xfef08a,
          emissive: 0xfff3a8,
          emissiveIntensity: 0.45,
          metalness: 0.3,
          roughness: 0.25,
        }),
      );
      halo.rotation.x = Math.PI / 2;
      halo.position.set(0, 2.96, 0);
      g.add(halo);
      break;
    }
  }
  return g;
}

function makeShirtMaterial(base: THREE.Color, pattern: string): THREE.MeshStandardMaterial {
  if (pattern === "solid") {
    return new THREE.MeshStandardMaterial({ color: base, roughness: 0.55, metalness: 0.03 });
  }
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#" + base.getHexString();
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = "#ffffff";
  if (pattern === "stripes") {
    const stripeH = size / 8;
    for (let i = 0; i < 8; i += 2) ctx.fillRect(0, i * stripeH, size, stripeH);
  } else if (pattern === "dots") {
    for (let y = 24; y < size; y += 48) {
      for (let x = 24; x < size; x += 48) {
        ctx.beginPath();
        ctx.arc(x + (Math.floor(y / 48) % 2 === 0 ? 0 : 24), y, 10, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  } else if (pattern === "star") {
    ctx.fillStyle = "#ffd166";
    const drawStar = (cx: number, cy: number, r: number) => {
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const ang = (i / 10) * Math.PI * 2 - Math.PI / 2;
        const rad = i % 2 === 0 ? r : r * 0.45;
        const x = cx + Math.cos(ang) * rad;
        const y = cy + Math.sin(ang) * rad;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
    };
    for (let y = 40; y < size; y += 80) {
      for (let x = 40; x < size; x += 80) {
        drawStar(x + (Math.floor(y / 80) % 2 === 0 ? 0 : 40), y, 22);
      }
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return new THREE.MeshStandardMaterial({ map: tex, roughness: 0.55, metalness: 0.03 });
}

function animateRunnerLegacy(runner: THREE.Object3D, t: number, jumping = false, jumpT = 1) {
  const parts = runner.userData.parts as
    | { body: THREE.Object3D; ll: THREE.Object3D; rl: THREE.Object3D }
    | undefined;
  if (!parts) return;
  const bounce = Math.abs(Math.sin(t * 3.2)) * 0.16;
  const jumpArc = jumping ? Math.sin(jumpT * Math.PI) : 0;
  const breath = Math.sin(t * 2.1) * 0.035;
  parts.body.position.y = jumping ? bounce * 0.24 : breath * 0.35;
  parts.body.rotation.x = jumping ? -0.1 - jumpArc * 0.18 : 0;
  parts.body.rotation.z = jumping ? Math.sin(t * 2.2) * 0.04 : 0;
  parts.body.rotation.y = 0;
  parts.body.scale.set(1 - breath * 0.12, 1 + breath, 1 - breath * 0.12);
  if (jumping) {
    parts.ll.rotation.x = -0.95 + jumpArc * 0.35;
    parts.rl.rotation.x = -0.95 + jumpArc * 0.35;
  } else {
    parts.ll.rotation.x = 0;
    parts.rl.rotation.x = 0;
  }
}

const skyCache = new Map<string, THREE.CanvasTexture>();
const iconTextureCache = new Map<string, THREE.Texture>();

function iconTexture(url: string) {
  const cached = iconTextureCache.get(url);
  if (cached) return cached;
  const tex = new THREE.TextureLoader().load(url);
  tex.colorSpace = THREE.SRGBColorSpace;
  iconTextureCache.set(url, tex);
  return tex;
}

let lobbyBackdrop: THREE.CanvasTexture | null = null;
function makeLobbyBackdropTexture() {
  if (lobbyBackdrop) return lobbyBackdrop;
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 1024;
  const ctx = canvas.getContext("2d")!;
  const grad = ctx.createRadialGradient(512, 450, 80, 512, 512, 760);
  grad.addColorStop(0, "#ffe96b");
  grad.addColorStop(0.42, "#ffc914");
  grad.addColorStop(1, "#ffb000");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1024, 1024);
  ctx.strokeStyle = "rgba(255,255,255,0.18)";
  ctx.lineWidth = 30;
  for (let r = 120; r < 900; r += 90) {
    ctx.beginPath();
    ctx.arc(512, 460, r, 0, Math.PI * 2);
    ctx.stroke();
  }
  lobbyBackdrop = new THREE.CanvasTexture(canvas);
  lobbyBackdrop.colorSpace = THREE.SRGBColorSpace;
  return lobbyBackdrop;
}

function makeSkyTexture(top: THREE.Color, bottom: THREE.Color) {
  const key = `${top.getHexString()}-${bottom.getHexString()}`;
  const cached = skyCache.get(key);
  if (cached) return cached;
  const canvas = document.createElement("canvas");
  canvas.width = 4;
  canvas.height = 512;
  const ctx = canvas.getContext("2d")!;
  const grad = ctx.createLinearGradient(0, 0, 0, 512);
  grad.addColorStop(0, `#${top.getHexString()}`);
  grad.addColorStop(1, `#${bottom.getHexString()}`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 4, 512);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  skyCache.set(key, tex);
  return tex;
}

const textCache = new Map<string, THREE.CanvasTexture>();
function makeTextTexture(text: string) {
  const cached = textCache.get(text);
  if (cached) return cached;
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = "900 58px Nunito, Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineWidth = 10;
  ctx.strokeStyle = "rgba(0, 95, 44, 0.75)";
  ctx.strokeText(text, 256, 66);
  ctx.fillStyle = "#ffffff";
  ctx.fillText(text, 256, 66);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  textCache.set(text, tex);
  return tex;
}
